/**
 * ErrorBoundary globale (feb-2026 hotfix P0 Fine Arts).
 *
 * Cattura errori di rendering React che altrimenti causano schermata bianca.
 * Reporta l'errore al backend (`/api/ai-site-doctor/report-error`) e mostra
 * fallback UI con azioni recovery (reload / torna home).
 *
 * Uso: wrappa <BrowserRouter> in App.js.
 */
import React from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", eventId: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: (error && error.message) || "Errore di rendering",
    };
  }

  componentDidCatch(error, info) {
    try {
      const payload = {
        error_type:      "react_error_boundary",
        message:         String((error && error.message) || error || "unknown")
                            .slice(0, 500),
        stack:           String((error && error.stack) || "").slice(0, 4000),
        component_stack: String((info && info.componentStack) || "").slice(0, 3000),
        url:             (typeof window !== "undefined" ? window.location.href : "").slice(0, 500),
        user_agent:      (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 300),
      };
      axios.post(`${API}/ai-site-doctor/report-error`, payload).catch(() => { /* silent */ });
    } catch { /* silent */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* ignore */ }
  };

  handleHome = () => {
    try { window.location.href = "/"; } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <main
        className="min-h-screen flex items-center justify-center bg-[#FCFCFA] px-4"
        role="alert"
        aria-live="assertive"
        data-testid="global-error-boundary"
      >
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round"
                 className="w-8 h-8 text-rose-600" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="font-heading text-2xl text-[#0F1F14] font-medium mb-2">
            Si è verificato un errore
          </h1>
          <p className="text-sm text-[#4A5E51] mb-6">
            La pagina non è riuscita a caricarsi correttamente. Il problema è stato segnalato al team tecnico.
          </p>
          <details className="text-xs text-neutral-400 mb-6 text-left bg-neutral-50 rounded-lg p-3 border border-neutral-200">
            <summary className="cursor-pointer font-medium text-neutral-500">Dettagli tecnici</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words">
              {this.state.message}
            </pre>
          </details>
          <div className="flex gap-2 justify-center">
            <button
              onClick={this.handleReload}
              className="px-4 py-2 rounded-lg bg-[#163321] text-white text-sm font-medium hover:bg-[#0F1F14] transition"
              data-testid="error-boundary-reload"
            >
              Ricarica pagina
            </button>
            <button
              onClick={this.handleHome}
              className="px-4 py-2 rounded-lg border border-[#163321]/30 text-[#163321] text-sm font-medium hover:bg-[#163321]/5 transition"
              data-testid="error-boundary-home"
            >
              Torna alla home
            </button>
          </div>
        </div>
      </main>
    );
  }
}
